import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useReducer,
  useState,
} from 'react'
import { ISurvey, ApiResponse } from '../typing'
import surveyReducer from './surveyReducer'

export interface PendingChange {
  sectionId: string
  questionId: string
  response: ApiResponse
}

export interface ISurveyContext {
  surveyState: ISurvey
  loadInitialSurvey: (survey: ISurvey) => void
  navigationHistory: string[]
  addToNavigationHistory: (questionId: string) => void
  removeLastFromNavigationHistory: () => void
  clearNavigationHistory: () => void
  pendingChanges: Map<string, PendingChange>
  setPendingChanges: Dispatch<SetStateAction<Map<string, PendingChange>>>
}

const defaultSurvey: ISurvey = {
  sections: {},
  startSectionId: '',
  id: '',
  name: '',
  businessId: '',
  status: '',
  description: '',
  startSectionBusinessId: '',
  surveyFor: '',
}

const defaultValue: ISurveyContext = {
  surveyState: defaultSurvey,
  loadInitialSurvey: () => {},
  navigationHistory: [],
  addToNavigationHistory: () => {},
  removeLastFromNavigationHistory: () => {},
  clearNavigationHistory: () => {},
  pendingChanges: new Map(),
  setPendingChanges: () => {},
}

export const SurveyContext = createContext(defaultValue)

const SurveyProvider: FC<PropsWithChildren> = ({ children }) => {
  const [surveyState, surveyDispatch] = useReducer(surveyReducer, {
    ...defaultSurvey,
  })

  const [navigationHistory, setNavigationHistory] = useState<string[]>(() => {
    try {
      const savedHistory = localStorage.getItem('surveyNavigationHistory')
      const parsedHistory = savedHistory ? JSON.parse(savedHistory) : []
      return Array.isArray(parsedHistory) ? parsedHistory : []
    } catch (error) {
      return []
    }
  })

  const addToNavigationHistory = (questionId: string) => {
    setNavigationHistory((prev) => {
      const newHistory = [...prev, questionId]
      localStorage.setItem(
        'surveyNavigationHistory',
        JSON.stringify(newHistory)
      )
      return newHistory
    })
  }

  const removeLastFromNavigationHistory = () => {
    setNavigationHistory((prev) => {
      const newHistory = prev.slice(0, -1)
      localStorage.setItem(
        'surveyNavigationHistory',
        JSON.stringify(newHistory)
      )
      return newHistory
    })
  }

  const loadInitialSurvey = useCallback((survey: ISurvey) => {
    surveyDispatch({ type: 'LOAD_INITIAL_SURVEY', payload: survey })
  }, [])

  const [pendingChanges, setPendingChanges] = useState<
    Map<string, PendingChange>
  >(new Map())

  const clearNavigationHistory = () => {
    setNavigationHistory([])
    localStorage.setItem('surveyNavigationHistory', JSON.stringify([]))
  }

  return (
    <SurveyContext.Provider
      value={{
        surveyState,
        loadInitialSurvey,
        navigationHistory,
        addToNavigationHistory,
        removeLastFromNavigationHistory,
        clearNavigationHistory,
        pendingChanges,
        setPendingChanges,
      }}
    >
      {children}
    </SurveyContext.Provider>
  )
}

export default SurveyProvider
